<template>
  <v-flex xs12>

    <AudioDialogSet
      :listItems="currentItemsList"
      :name="audioDescription"
      :show.sync="setAudioDialog.show"
      :audioId="audioId"
      :toDefault="setAudioDialog.toDefault"
      :playlistType="currentPlaylistType"
      @set="setAudio"
      @cancel="cancelSetDialog"
    />

    <AudioDialogUnset
      :name="audioDescription"
      :show.sync="unsetAudioDialog.show"
      :audioId="audioId"
      :fromId="activePlaylist.listId"
      :playlistType="currentPlaylistType"
      @unset="unsetAudio"
      @cancel="cancelUnsetDialog"
    />

    <AudioDialogDelete
      v-if="!hasSomeAbilities($constants.ABILITY.INTERN)"
      :name="audioDescription"
      :show.sync="deleteAudioDialog.show"
      :audioId="audioId"
      @deleted="deleteAudio"
      @cancel="cancelDeleteDialog"
    />

    <v-dialog
      v-model="audioInfoDialog.show"
      max-width="75%"
    >
      <v-card>
        <v-card-title>
          {{ audioInfoDialog.title }}
        </v-card-title>

        <v-card-text>
          <v-data-table
                :items-per-page="10"
                :headers="audioInfoDialog.headers"
                :items="audioInfoDialog.items"
                :loading="audioInfoDialog.loading"
                loading-text="Loading... Please wait"
              >
                <template v-slot:item.audiable_id="{ item }">
                  {{ item.audiable_id}}
                </template>

                <template v-slot:item.name="{ item }">
                  {{ item.name }}
                </template>

                <template v-slot:item.link="{ item }">
                  <v-btn
                    v-if="item.audiable_id > 0"
                    icon
                    :to="`${item.audiable_type}/${item.audiable_id}`"
                  >
                    <v-icon
                      color="orange"
                    >
                      mdi-pen
                    </v-icon>
                  </v-btn>
                </template>

              </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            text
            @click="audioInfoDialog.show = false"
          >
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>

    </v-dialog>

    <v-expansion-panels>
      <v-expansion-panel>
        <v-expansion-panel-header>
          Audio library ({{ libraryAudio.items.length }})
        </v-expansion-panel-header>
        <v-expansion-panel-content>

          <AudioList
            :items="libraryAudio.items"
            :loading="libraryAudio.loading"
          >
            <template v-slot:additional="{ item }">
              <v-chip
                v-for="audiables in item.counts"
                :key="audiables.audiable_type"
                class="mx-1"
                small
                outlined
                @click="callAudioInfoDialog(item, audiables.audiable_type)"
              >
                {{ audiables.audiable_type }}: {{ audiables.counts }}
              </v-chip>
            </template>

            <template v-slot:actions="slotProps">
              <v-btn icon @click="callSetAudioDialog(slotProps.item.id)" >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <slot>
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="cyan darken-3"
                      >
                        mdi-clipboard-arrow-right-outline
                      </v-icon>
                    </slot>
                  </template>
                  set to {{ currentPlaylistType }}
                </v-tooltip>
              </v-btn>
              <v-btn icon @click="callSetAudioDialog(slotProps.item.id, true)" >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <slot>
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="blue darken-1"
                      >
                        mdi-dropbox
                      </v-icon>
                    </slot>
                  </template>
                  set to default {{ currentPlaylistType }}
                </v-tooltip>
              </v-btn>
              <v-btn
                v-if="!hasSomeAbilities($constants.ABILITY.INTERN)"
                icon @click="callDeleteAudioDialog(slotProps.item.id)"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }" >
                    <slot>
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="red"
                      >
                        mdi-delete
                      </v-icon>
                    </slot>
                  </template>
                  delete audio
                </v-tooltip>
              </v-btn>
            </template>
            <template v-slot:top>
              <AudioDialogAdd
                playlist-type="library"
                @addNew="addAudioToLibrary"
                />
            </template>
          </AudioList>

        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-tabs
      v-model="tab"
      align-with-title
    >
      <v-tabs-slider color="yellow"></v-tabs-slider>

      <v-tab
        v-for="(item) in tabItems"
        :key="item.tab"
      >
        {{ item.tab }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item
        v-for="(item, key) in tabItems"
        :key="item.tab"
      >
        <v-card flat>
          <v-card-text>
            <v-data-table
              class="row-pointer"
              :items-per-page="10"
              :headers="item.headers"
              :items="item.items"
              :custom-sort="customSort"
              :single-expand="true"
              :expanded.sync="item.expanded"
              :search="item.commonSearch"
              :loading="item.loading"
              must-sort
              :sort-by.sync="item.sortBy"
              :sort-desc.sync="item.sortDesc"
              @click:row="rowClick"
              show-expand
              @item-expanded="loadDetails"
              loading-text="Loading... Please wait"
            >
              <template v-slot:top>
                <v-toolbar
                  flat
                >
                  <v-text-field
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                    v-model="item.commonSearch"
                  />
                  <v-spacer></v-spacer>
                  <AudioDialogAdd
                    :list-items="currentItemsList"
                    :playlistType="currentPlaylistType"
                    @addNew="addAudio"
                  />
                </v-toolbar>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn v-if="item.id > 0" icon :to="`${key}/${item.id}`">
                  <v-icon
                    color="orange"
                  >
                    mdi-pen
                  </v-icon>
                </v-btn>
                <v-icon v-else
                  color="blue darken-1"
                >
                  mdi-dropbox
                </v-icon>
              </template>

              <template v-slot:expanded-item="{ headers }">
                <td :colspan="headers.length">
                  <AudioList
                    :items="activePlaylist.items"
                  >
                    <template v-slot:actions="slotProps">
                      <v-btn icon @click="callSetAudioDialog(slotProps.item.id)" >
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <slot>
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                color="cyan darken-3"
                              >
                                mdi-clipboard-arrow-right-outline
                              </v-icon>
                            </slot>
                          </template>
                          set to geotag
                        </v-tooltip>
                      </v-btn>
                      <v-btn v-if="activePlaylist.listId!==-1"
                        icon
                        @click="callSetAudioDialog(slotProps.item.id, true)" >
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <slot>
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                color="blue darken-1"
                              >
                                mdi-dropbox
                              </v-icon>
                            </slot>
                          </template>
                          set to default
                        </v-tooltip>
                      </v-btn>
                      <v-btn
                        icon
                        @click="callUnsetAudioDialog(slotProps.item.id)" >
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <slot>
                              <v-icon
                                v-bind="attrs"
                                v-on="on"
                                color="orange darken-4"
                              >
                                mdi-cancel
                              </v-icon>
                            </slot>
                          </template>
                          unset audio
                        </v-tooltip>
                      </v-btn>
                    </template>
                  </AudioList>

                </td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-flex>
</template>

<script>
import { mapActions } from 'vuex';
import AudioDialogDelete from '../components/dialogs/AudioDialogDelete.vue';
import AudioList from '../components/AudioList.vue';
import AudioDialogAdd from '../components/dialogs/AudioDialogAdd.vue';
import AudioDialogSet from '../components/dialogs/AudioDialogSet.vue';
import AudioDialogUnset from '../components/dialogs/AudioDialogUnset.vue';

export default {
  name: 'Audio',
  components: {
    AudioDialogDelete,
    AudioList,
    AudioDialogAdd,
    AudioDialogSet,
    AudioDialogUnset,
  },
  data() {
    return {
      dialogupdating: false,
      audioId: null,
      toRequestAudioList: false,
      audioItems: [],
      activePlaylist: {
        listId: null,
        type: '',
        items: [],
        loading: true,
      },
      audioInfoDialog: {
        loading: false,
        title: '',
        show: false,
        audioId: null,
        type: '',
        headers: [
          { text: 'audiable id', value: 'audiable_id' },
          { text: 'audiable name', value: 'name' },
          { text: 'link', value: 'link' },
        ],
        items: [],
      },
      deleteAudioDialog: {
        show: false,
      },
      setAudioDialog: {
        show: false,
        toDefault: false,
      },
      unsetAudioDialog: {
        show: false,
      },
      newAudioDialog: {
        show: false,
      },
      libraryAudio: {
        loading: true,
        items: [],
      },
      items: [],
      tab: null,
      tabItems: {
        geotag: {
          tab: 'geotags',
          headers: [
            { text: 'geotag', value: 'name' },
            { text: 'audio amount', value: 'audio_count' },
            { text: '', value: 'actions', sortable: false },
            { text: '', value: 'data-table-expand' },
          ],
          items: [],
          expanded: [],
          commonSearch: '',
          sortBy: 'name',
          sortDesc: false,
          loading: true,
        },
        poi: {
          tab: 'poi',
          headers: [],
          items: [],
          expanded: [],
          commonSearch: '',
          sortBy: 'name',
          sortDesc: false,
          loading: false,
        },
      },
    };
  },

  created() {
    this.requestLibrary();
    this.requestPlaylists();
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    requestLibrary() {
      this.libraryAudio.loading = true;
      this.$requestHelpers
        .get('api/admin/audio')
        .then((response) => {
          this.libraryAudio.items = response.data;
          this.libraryAudio.loading = false;
        });
    },

    requestPlaylists(playlistType = 'geotag') {
      this.$requestHelpers
        .get(`api/admin/audio/playlists/${playlistType}`)
        .then((response) => {
          this.tabItems[playlistType].items = response.data;
          this.tabItems[playlistType].loading = false;
        });
    },

    rowClick(item, slot) {
      slot.expand(!slot.isExpanded);
    },

    loadDetails({ item, value }) {
      if (value) {
        this.activePlaylist.listId = Number(item.id);
        this.audioListRequest();
      }
    },

    callAudioInfoDialog(audio, type) {
      this.audioInfoDialog.loading = true;
      this.audioInfoDialog.show = true;
      this.audioInfoDialog.title = `${type}: ${audio.description} (ID:${audio.id})`;
      return this.$requestHelpers
        .get(`api/admin/audio/${audio.id}`,
          { params: { type } })
        .then((response) => {
          this.audioInfoDialog.loading = false;
          this.audioInfoDialog.items = response.data.audiables;
        });
    },

    callSetAudioDialog(audioId, toDefault = false) {
      this.audioId = audioId;
      this.setAudioDialog.show = true;
      this.setAudioDialog.toDefault = toDefault;
    },

    callUnsetAudioDialog(audioId) {
      this.audioId = audioId;
      this.unsetAudioDialog.show = true;
    },

    callDeleteAudioDialog(audioId) {
      this.audioId = audioId;
      this.deleteAudioDialog.show = true;
    },

    cancelSetDialog() {
      this.setAudioDialog.show = false;
      this.audioId = null;
    },

    cancelUnsetDialog() {
      this.setAudioDialog.show = false;
      this.audioId = null;
    },

    cancelDeleteDialog() {
      this.deleteAudioDialog.show = false;
      this.audioId = null;
    },

    setAudio(status) {
      if (!status) {
        this.setSnack({
          color: 'red',
          text: 'error',
        });
        return;
      }
      this.setSnack({
        color: 'green',
        text: 'saved',
      });

      this.requestPlaylists();
      this.audioListRequest();
      this.setAudioDialog.show = false;
      this.audioId = null;
    },

    unsetAudio(status) {
      if (!status) {
        this.setSnack({
          color: 'red',
          text: 'error',
        });
      } else {
        this.setSnack({
          color: 'green',
          text: 'saved',
        });
      }

      this.requestPlaylists();
      this.audioListRequest();
      this.unsetAudioDialog.show = false;
      this.audioId = null;
    },

    addAudioToLibrary(status) {
      if (!status) {
        this.setSnack({
          color: 'red',
          text: 'error',
        });
        return;
      }
      this.requestLibrary();
    },

    addAudio(status) {
      if (!status) {
        this.setSnack({
          color: 'red',
          text: 'error',
        });
        return;
      }

      this.setSnack({
        color: 'green',
        text: 'saved',
      });

      this.libraryAudio.loading = false;
      this.$requestHelpers.get('api/admin/audio')
        .then((response) => {
          this.libraryAudio.items = response.data;
          this.libraryAudio.loading = false;
          return this.$requestHelpers
            .get('api/admin/audio/playlists/geotag');
        }).then((response) => {
          this.tabItems.geotag.items = response.data;
          this.tabItems.geotag.loading = false;
          this.audioListRequest();
        });
    },

    deleteAudio(status) {
      if (!status) {
        this.setSnack({
          color: 'red',
          text: 'error',
        });
      } else {
        this.setSnack({
          color: 'green',
          text: 'saved',
        });
        this.requestLibrary();
        this.requestPlaylists();
        this.audioListRequest();
      }

      this.deleteAudioDialog.show = false;
      this.audioId = null;
    },

    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        const aVal = a[index[0]];
        const bVal = b[index[0]];

        if (a.name === 'Default') {
          return -1;
        }
        if (b.name === 'Default') {
          return 1;
        }

        if (!isDesc[0]) {
          return aVal < bVal ? -1 : 1;
        }
        return aVal > bVal ? -1 : 1;
      });
      return items;
    },

    audioListRequest() {
      if (!this.activePlaylist.listId) {
        return;
      }

      this.activePlaylist.loading = true;
      this.activePlaylist.items = [];
      this.$requestHelpers
        .get(`api/admin/audio/playlist/${this.currentPlaylistType}/${this.activePlaylist.listId}`)
        .then((response) => {
          this.activePlaylist.items = response.data;
          this.activePlaylist.loading = false;
        });
    },

  },

  computed: {
    currentPlaylistType() {
      switch (this.tab) {
        case 1:
          return 'poi';
        case 0:
        default:
          return 'geotag';
      }
    },
    currentItemsList() {
      return this.tabItems[this.currentPlaylistType].items;
    },
    geotagsList() {
      return this.items.map((item) => ({
        id: item.id,
        name: item.name,
      }));
    },
    audioDescription() {
      return this.libraryAudio.items.reduce(
        (prev, item) => `${prev}${item.id === this.audioId ? item.description : ''}`, '',
      );
    },
  },

};
</script>

<style lang="css" scoped>
  .row-pointer >>> tbody tr:not(.v-data-table__expanded__content):hover {
    cursor: pointer;
  }
</style>
